import React, { useState, useEffect } from 'react';
import Pagination from './Pagination';
import { useAuth } from './AuthContext';
import { groupByCustomer, formatFollowUpDate, localDateStr, getKnownAgents } from './callUtils';
import { DownloadIcon, SearchIcon } from './Icons';
import { downloadCsv } from './csvExport';
import CallHistoryModal from './CallHistoryModal';

const DEFAULT_PAGE_SIZE = 50;

// Keep in sync with FOLLOW_UP_TYPE_OPTIONS / FOLLOW_UP_PRIORITY_OPTIONS in
// CallHistoryModal.js.
const FOLLOW_UP_TYPE_LABELS = {
  track_link: 'Track Link',
  ndr_rto: 'NDR/RTO',
  exchange: 'Exchange',
  order: 'Order',
  new_product: 'New Product',
  other: 'Other',
};

const FOLLOW_UP_PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

// Every follow-up ever logged across every customer (one row per follow-up,
// not one per customer — a customer can have several over time, see
// CallHistoryModal's Follow Up section), lined up against that number's
// most recent call so an admin can work a follow-up list without opening
// each customer's history individually.
function FollowUpPanel({ calls }) {
  const { authFetch, user } = useAuth();
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending' | 'done' | 'all'
  // Same identity MyDashboard uses to attribute "my" follow-ups — whoever
  // created it, matched against agent_name (falling back to username).
  const myIdentity = user?.agentName || user?.username;
  const [mineOnly, setMineOnly] = useState(false);
  // Customer number whose call-history modal is open — clicking a row jumps
  // straight to that customer's history, same as the Incoming Calls table.
  const [historyNumber, setHistoryNumber] = useState(null);

  const fetchFollowUps = async () => {
    try {
      const response = await authFetch('/api/follow-ups');
      const data = await response.json();
      setFollowUps(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUps();
  }, []);

  const groupedCalls = groupByCustomer(calls);
  const callsByNumber = new Map(groupedCalls.map((group) => [group.customerNumber, group.latestCall]));
  const knownAgents = getKnownAgents(calls);

  const now = Date.now();
  const isOverdue = (followUp) =>
    followUp.follow_up_status !== 'done' &&
    followUp.follow_up_date &&
    new Date(followUp.follow_up_date).getTime() < now;

  const statusRows =
    statusFilter === 'all'
      ? followUps
      : followUps.filter((followUp) => (followUp.follow_up_status || 'pending') === statusFilter);

  const mineRows = mineOnly ? statusRows.filter((followUp) => followUp.created_by === myIdentity) : statusRows;

  const searchTerm = search.trim().toLowerCase();
  const rows = !searchTerm
    ? mineRows
    : mineRows.filter((followUp) => {
        const call = callsByNumber.get(followUp.customer_number);
        return (
          (followUp.customer_name || '').toLowerCase().includes(searchTerm) ||
          followUp.customer_number.toLowerCase().includes(searchTerm) ||
          (followUp.customer_order_number || '').toLowerCase().includes(searchTerm) ||
          (followUp.follow_up_note || '').toLowerCase().includes(searchTerm) ||
          (followUp.reschedule_reason || '').toLowerCase().includes(searchTerm) ||
          (followUp.resolution_remark || '').toLowerCase().includes(searchTerm) ||
          (FOLLOW_UP_TYPE_LABELS[followUp.follow_up_type] || '').toLowerCase().includes(searchTerm) ||
          (followUp.resolved_by || followUp.created_by || call?.answered_agent_name || '')
            .toLowerCase()
            .includes(searchTerm)
        );
      });

  // High priority first, then medium, then low/unset — soonest due first
  // within each priority tier, with no-date rows sinking to the bottom.
  const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
  const sortedRows = [...rows].sort((a, b) => {
    const aRank = PRIORITY_RANK[a.follow_up_priority] ?? 3;
    const bRank = PRIORITY_RANK[b.follow_up_priority] ?? 3;
    if (aRank !== bRank) return aRank - bRank;
    const aTime = a.follow_up_date ? new Date(a.follow_up_date).getTime() : Infinity;
    const bTime = b.follow_up_date ? new Date(b.follow_up_date).getTime() : Infinity;
    return aTime - bTime;
  });

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, mineOnly]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const handleExportCsv = () => {
    const headers = [
      'Customer Name',
      'Number',
      'Order Number',
      'Follow-up Type',
      'Priority',
      'Status',
      'Follow-up Date/Time',
      'Follow-up Note',
      'Reschedule Reason',
      'Resolution',
      'Agent Name',
      'Created At',
    ];
    const exportRows = sortedRows.map((followUp) => [
      followUp.customer_name || '',
      followUp.customer_number,
      followUp.customer_order_number || '',
      FOLLOW_UP_TYPE_LABELS[followUp.follow_up_type] || '',
      FOLLOW_UP_PRIORITY_LABELS[followUp.follow_up_priority] || '',
      followUp.follow_up_status === 'done' ? 'Done' : 'Pending',
      followUp.follow_up_date || '',
      followUp.follow_up_note || '',
      followUp.reschedule_reason || '',
      followUp.resolution_remark || '',
      followUp.resolved_by ||
        followUp.created_by ||
        callsByNumber.get(followUp.customer_number)?.answered_agent_name ||
        '',
      followUp.created_at ? formatFollowUpDate(followUp.created_at) : '',
    ]);
    downloadCsv(`follow-ups-${localDateStr(new Date())}.csv`, headers, exportRows);
  };

  if (loading) {
    return <div className="loading-state">Loading…</div>;
  }

  return (
    <>
      <div className="incoming-filters-bar">
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, number, order number, note, or agent"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search follow-ups"
          />
        </div>
        <div className="customer-toggle" role="group" aria-label="Filter by follow-up status">
          <button
            type="button"
            className={statusFilter === 'pending' ? 'active follow-up-filter-pending' : ''}
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </button>
          <button
            type="button"
            className={statusFilter === 'done' ? 'active follow-up-filter-done' : ''}
            onClick={() => setStatusFilter('done')}
          >
            Done
          </button>
          <button type="button" className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>
            All
          </button>
        </div>
        {myIdentity && (
          <div className="customer-toggle" role="group" aria-label="Filter by ownership">
            <button type="button" className={!mineOnly ? 'active' : ''} onClick={() => setMineOnly(false)}>
              All Agents
            </button>
            <button type="button" className={mineOnly ? 'active' : ''} onClick={() => setMineOnly(true)}>
              My Follow Ups
            </button>
          </div>
        )}
        <button type="button" className="btn btn-secondary export-csv-btn" onClick={handleExportCsv}>
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Number</th>
              <th>Order Number</th>
              <th>Follow-up Type</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Follow-up Date/Time</th>
              <th>Follow-up Note</th>
              <th>Reschedule Reason</th>
              <th>Resolution</th>
              <th>Agent Name</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan="12" style={{ textAlign: 'center' }}>
                  {followUps.length === 0 ? 'No follow-ups saved yet.' : 'No matching follow-ups found.'}
                </td>
              </tr>
            ) : (
              pageRows.map((followUp) => {
                const call = callsByNumber.get(followUp.customer_number);
                const overdue = isOverdue(followUp);
                // Only high priority (and overdue) tints the whole row — low/medium
                // stay plain, with the Priority badge itself still color-coded.
                const rowClass = overdue
                  ? 'row-critical'
                  : followUp.follow_up_priority === 'high'
                  ? 'row-priority-high'
                  : '';
                return (
                  <tr key={followUp.id} className={rowClass}> 
                    <td className="table-cell-strong">{followUp.customer_name || '-'}</td>
                    <td>
                      <button
                        type="button"
                        className="customer-number-link"
                        onClick={() => setHistoryNumber(followUp.customer_number)}
                        title="View call history for this number"
                      >
                        {followUp.customer_number}
                      </button>
                    </td>
                    <td>{followUp.customer_order_number || '-'}</td>
                    <td>{FOLLOW_UP_TYPE_LABELS[followUp.follow_up_type] || '-'}</td>
                    <td>
                      {followUp.follow_up_priority ? (
                        <span className={`status follow-up-priority-${followUp.follow_up_priority}`}>
                          {FOLLOW_UP_PRIORITY_LABELS[followUp.follow_up_priority]}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span
                        className={`status ${
                          followUp.follow_up_status === 'done' ? 'follow-up-status-done' : 'follow-up-status-pending'
                        }`}
                      >
                        {followUp.follow_up_status === 'done' ? 'Done' : 'Pending'}
                      </span>
                      {overdue && <span className="status critical table-critical-badge">Overdue</span>}
                    </td>
                    <td>{followUp.follow_up_date ? formatFollowUpDate(followUp.follow_up_date) : '-'}</td>
                    <td className="table-cell-truncate" title={followUp.follow_up_note || ''}>
                      {followUp.follow_up_note || '-'}
                    </td>
                    <td className="table-cell-truncate" title={followUp.reschedule_reason || ''}>
                      {followUp.reschedule_reason || '-'}
                    </td>
                    <td>
                      <span className="table-cell-truncate" title={followUp.resolution_remark || ''}>
                        {followUp.resolution_remark || '-'}
                      </span>
                      {followUp.attachment_url && (
                        <>
                          {' '}
                          <a
                            href={followUp.attachment_url}
                            target="_blank"
                            rel="noreferrer"
                            className="follow-up-history-attachment"
                          >
                            📎
                          </a>
                        </>
                      )}
                    </td>
                    {/* Whoever resolved it takes priority, then whoever created it —
                        both are more meaningful than the customer's latest call agent. */}
                    <td>{followUp.resolved_by || followUp.created_by || call?.answered_agent_name || '-'}</td>
                    <td>{followUp.created_at ? formatFollowUpDate(followUp.created_at) : '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={sortedRows.length}
      />

      {historyNumber && (
        <CallHistoryModal
          group={groupedCalls.find((group) => group.customerNumber === historyNumber)}
          onClose={() => {
            setHistoryNumber(null);
            // Refresh in case a follow-up was added/edited/marked done while
            // the modal was open, so the table reflects it immediately.
            fetchFollowUps();
          }}
          agents={knownAgents}
          onCallTriggered={() => {}}
          initialShowFollowUp
        />
      )}
    </>
  );
}

export default FollowUpPanel;
